import React, { useState, useEffect, useContext, useRef } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import MisGirasTableComponent from "../../components/tables/MisGirasTableComponent";
import ConvenioModal from "../../components/ConvenioModal";
import InformacionAceptadaModal from "../../components/InformacionAceptadaModal";
import SolicitudCupoModal from "../../components/SolicitudCupoModal";
import MisSolicitudesCupoComponent from "../../components/MisSolicitudesCupoComponent";

import PageLayout from "../../layouts/PageLayout";
import products from "../../assets/data/misgiras.json";

import { getMisGiras, verificarInformacionAceptada, aceptarInformacion, obtenerResponsableGira } from "../../api/giras.ts";
import { verificarConvenio } from "../../api/convenios.api.ts";

export default function MisGirasListPage() {

    const { t } = useContext(TranslatorContext)

    const [giras, setGiras] = useState([]);
    const [showConvenioModal, setShowConvenioModal] = useState(false);
    const [showInformacionModal, setShowInformacionModal] = useState(false);
    const [showSolicitudCupoModal, setShowSolicitudCupoModal] = useState(false);
    const [alumnoActual, setAlumnoActual] = useState(null);
    const [giraSolicitudActual, setGiraSolicitudActual] = useState(null);
    const [conveniosVerificados, setConveniosVerificados] = useState({});
    const [informacionAceptada, setInformacionAceptada] = useState(false);
    const [procesandoInformacion, setProcesandoInformacion] = useState(false);
    const [responsablesGira, setResponsablesGira] = useState({});
    const [refreshSolicitudes, setRefreshSolicitudes] = useState(0);
    const verificacionIniciada = useRef(false);

    useEffect(() => {
         const fetchTabla = async () => {
             // Verificar si la información fue aceptada
             const verificacion = await verificarInformacionAceptada();
             if (verificacion.aceptada) {
                 setInformacionAceptada(true);
             } else {
                 setShowInformacionModal(true);
             }

             const res = await getMisGiras();
             setGiras(res);
             
             // Verificar si el usuario es responsable de cada gira
             if (res && res.length > 0) {
                 const responsables = {};
                 for (const gira of res) {
                     const responsable = await obtenerResponsableGira(gira.gir_id);
                     console.log(`Responsable de Gira ${gira.gir_id}:`, responsable);
                     responsables[gira.gir_id] = responsable?.responsable || null;
                 }
                 console.log("Responsables de Gira:", responsables);
                 setResponsablesGira(responsables);
             }
             
             // Verificar convenios para cada alumno solo una vez (pero solo si información fue aceptada)
             if (res && res.length > 0 && !verificacionIniciada.current && verificacion.aceptada) {
                 verificacionIniciada.current = true;
                 verificarConveniosPendientes(res);
             }
         }
    
         fetchTabla();   
         // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);

    /**
     * Maneja cuando se acepta la información
     */
    const handleInformacionAceptada = async () => {
        try {
            setProcesandoInformacion(true);
            const res = await aceptarInformacion();
            
            if (!res.error) {
                setInformacionAceptada(true);
                setShowInformacionModal(false);
                
                // Cargar giras nuevamente y verificar convenios
                const girasRes = await getMisGiras();
                setGiras(girasRes);
                
                if (girasRes && girasRes.length > 0 && !verificacionIniciada.current) {
                    verificacionIniciada.current = true;
                    verificarConveniosPendientes(girasRes);
                }
            }
        } catch (err) {
            console.error("Error al aceptar información:", err);
        } finally {
            setProcesandoInformacion(false);
        }
    };

    /**
     * Verifica si hay convenios pendientes de aceptar
     */
    const verificarConveniosPendientes = async (girasData) => {
        try {
            const verificados = {};
            
            // Verificar cada alumno
            for (const gira of girasData) {
                if (gira.alu_id && gira.gir_id) {
                    const key = `${gira.alu_id}_${gira.gir_id}`;
                    
                    // Evitar verificar si ya fue verificado en esta sesión
                    if (conveniosVerificados[key]) {
                        continue;
                    }
                    
                    try {
                        const resultado = await verificarConvenio(gira.alu_id, gira.gir_id);
                        
                        verificados[key] = !resultado.necesitaAceptar;
                        
                        // Si necesita aceptar y no hemos mostrado aún ningún modal
                        if (resultado.necesitaAceptar && !alumnoActual) {
                            setAlumnoActual(gira);
                            setShowConvenioModal(true);
                            break; // Solo mostrar uno a la vez
                        }
                    } catch (err) {
                        console.error(`Error verificando convenio para alumno ${gira.alu_id}:`, err);
                    }
                }
            }
            
            setConveniosVerificados(prev => ({ ...prev, ...verificados }));
        } catch (err) {
            console.error("Error verificando convenios:", err);
        }
    };

    /**
     * Maneja cuando se acepta un convenio
     */
    const handleConvenioAceptado = () => {
        if (alumnoActual) {
            const key = `${alumnoActual.alu_id}_${alumnoActual.gir_id}`;
            setConveniosVerificados(prev => ({
                ...prev,
                [key]: true
            }));
        }
        
        setShowConvenioModal(false);
        setAlumnoActual(null);
        
        // Verificar si hay más convenios pendientes
        const alumnosPendientes = giras.filter(gira => {
            const key = `${gira.alu_id}_${gira.gir_id}`;
            return !conveniosVerificados[key] && gira.alu_id !== alumnoActual?.alu_id;
        });
        
        if (alumnosPendientes.length > 0) {
            // Mostrar el siguiente convenio pendiente
            setTimeout(() => {
                verificarConveniosPendientes(alumnosPendientes);
            }, 1000);
        }
    };

    /**
     * Maneja cuando se cierra el modal sin aceptar
     */
    const handleConvenioClose = () => {
        setShowConvenioModal(false);
        // No resetear alumnoActual para evitar que se vuelva a mostrar
    };

    /**
     * Abre el modal para solicitar nuevo cupo
     */
    const handleAbrirSolicitudCupo = (gira) => {
        setGiraSolicitudActual(gira);
        setShowSolicitudCupoModal(true);
    };

    /**
     * Maneja cuando se cierra el modal de solicitud
     */
    const handleCerrarSolicitudCupo = () => {
        setShowSolicitudCupoModal(false);
        setGiraSolicitudActual(null);
    };

    /**
     * Maneja cuando la solicitud se envía exitosamente
     */
    const handleSolicitudCupoEnviada = () => {
        setRefreshSolicitudes(prev => prev + 1);
    };

    return (
        <PageLayout>
            <Row>
               
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Mis Giras</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                              
                                <li className="mc-breadcrumb-item">Giras</li>
                            </ul>
                        </div>
                    </div>
                </Col>
               
                <Col xl={12} >
                    <div className="mc-card">
                        <Row  style={{ minHeight: '650px' }}>
                            
                            <Col xl={12}>
                                <MisGirasTableComponent 
                                    thead={ products.thead } 
                                    data={giras}
                                    informacionAceptada={informacionAceptada}
                                />
                                
                                {/* Sección de Solicitud de Cupos */}
                                {giras && giras.length > 0 && (
                                    <div style={{ marginTop: '40px' }}>
                                        <h4 style={{ marginBottom: '25px', fontWeight: 'bold', color: '#2c3e50' }}>
                                            <i className='material-icons' style={{ fontSize: '1.5rem', verticalAlign: 'middle', marginRight: '10px' }}>person_add</i>
                                            Solicitud de Nuevos Cupos
                                        </h4>
                                        
                                        {giras.map((gira, index) => {
                                            const esResponsable = responsablesGira[gira.gir_id]?.usu_id !== undefined;
                                            
                                            if (!esResponsable) return null;
                                            
                                            return (
                                                <div key={index} style={{ 
                                                    marginBottom: '25px',
                                                    padding: '20px',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '8px',
                                                    borderLeft: '4px solid #2196F3'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                                        <div>
                                                            <h5 style={{ margin: 0, color: '#2c3e50' }}>
                                                                <strong>{gira.gir_curso || 'Gira sin nombre'}</strong>
                                                            </h5>
                                                            <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                                                                <i className='material-icons' style={{ fontSize: '0.9rem', verticalAlign: 'middle' }}>check_circle</i>
                                                                Eres responsable de esta gira
                                                            </p>
                                                        </div>
                                                        <button 
                                                            className="btn btn-primary"
                                                            onClick={() => handleAbrirSolicitudCupo(gira)}
                                                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                                        >
                                                            <i className='material-icons' style={{ fontSize: '1.1rem' }}>person_add</i>
                                                            Solicitar Nuevo Cupo
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Tabla de solicitudes */}
                                                    <div style={{ marginTop: '15px' }}>
                                                        <h6 style={{ marginBottom: '10px', fontWeight: 'bold', color: '#555' }}>
                                                            <i className='material-icons' style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: '5px' }}>list</i>
                                                            Mis Solicitudes para esta Gira
                                                        </h6>
                                                        <MisSolicitudesCupoComponent 
                                                            giraId={gira.gir_id}
                                                            onRefresh={refreshSolicitudes}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        
                                        {giras.length > 0 && !giras.some(g => responsablesGira[g.gir_id]?.usu_id !== undefined) && (
                                            <div style={{ 
                                                textAlign: 'center',
                                                padding: '30px',
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '4px'
                                            }}>
                                                <p style={{ color: '#999', margin: 0 }}>
                                                    <i className='material-icons' style={{ fontSize: '2rem', display: 'block', marginBottom: '10px' }}>info</i>
                                                    No eres responsable de ninguna gira
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
            
            {/* Modal de Información */}
            <InformacionAceptadaModal
                show={showInformacionModal && !informacionAceptada}
                onConfirmed={handleInformacionAceptada}
                loading={procesandoInformacion}
            />
            
            {/* Modal de Convenio - Solo se muestra después de aceptar la información */}
            {alumnoActual && !informacionAceptada && (
                <ConvenioModal
                    show={showConvenioModal}
                    aluId={alumnoActual.alu_id}
                    girId={alumnoActual.gir_id}
                    alumnoNombre={`${alumnoActual.alu_nombres} ${alumnoActual.alu_apellidos}`}
                    onAccepted={handleConvenioAceptado}
                    onClose={handleConvenioClose}
                />
            )}

            {/* Modal de Solicitud de Cupo */}
            {giraSolicitudActual && (
                <SolicitudCupoModal
                    show={showSolicitudCupoModal}
                    onHide={handleCerrarSolicitudCupo}
                    giraId={giraSolicitudActual.gir_id}
                    onSuccess={handleSolicitudCupoEnviada}
                />
            )}
        </PageLayout>
    );
}
